import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PublicFooter, PublicHeader } from "../../../components/PublicLayout";
import { useTextGuide } from "../../../api/textGuide";
import { CommandDeck } from "./CommandDeck";
import { Compliance, GuideSections, PrintSheet } from "./GuideSections";
import { JourneyHeader } from "./JourneyHeader";
import { JourneyStageNavigation } from "./JourneyStageNavigation";
import { buildJourney, type JourneyCommand, type Stage } from "./journeyData";

const CommandDirectory = lazy(() => import("./CommandDirectory").then((m) => ({ default: m.CommandDirectory })));

const TITLE = "GamedayTime Text Guide: every crew text code";
const DESC = "Every GamedayTime crew text code in one place: IN, OUT, BREAK, BACK, SHIFTS, HOURS, LATE, FIX, CONFIRM, DECLINE, STATUS, HELP, STOP and START, with example replies.";

function useMeta() {
  useEffect(() => {
    const prev = document.title;
    const tag = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const prevDesc = tag?.content ?? "";
    document.title = TITLE;
    tag?.setAttribute("content", DESC);
    return () => { document.title = prev; tag?.setAttribute("content", prevDesc); };
  }, []);
}

const fromHash = (cmds: JourneyCommand[]) => Math.max(0, cmds.findIndex((c) => `#${c.slug}` === window.location.hash.toLowerCase()));

/** /text#in ⇄ current card. Swipes replace the entry; stage jumps and "Open in journey" push one, so Back works. */
function useHashIndex(cmds: JourneyCommand[]) {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (!cmds.length) return;
    setIndex(fromHash(cmds));
    const sync = () => { if (cmds.some((c) => `#${c.slug}` === window.location.hash.toLowerCase())) setIndex(fromHash(cmds)); };
    window.addEventListener("popstate", sync);
    window.addEventListener("hashchange", sync);
    return () => { window.removeEventListener("popstate", sync); window.removeEventListener("hashchange", sync); };
  }, [cmds]);
  const set = useCallback((i: number, push = false) => {
    setIndex(i);
    const hash = `#${cmds[i]?.slug}`;
    if (cmds[i] && window.location.hash !== hash) window.history[push ? "pushState" : "replaceState"](window.history.state, "", hash);
  }, [cmds]);
  return [index, set] as const;
}

export default function ShiftJourneyPage() {
  useMeta();
  const { guide, failed } = useTextGuide();
  const cmds = useMemo(() => (guide ? buildJourney(guide.commands) : []), [guide]);
  const [index, setIndex] = useHashIndex(cmds);
  const [dirOpen, setDirOpen] = useState(false);
  const deckRef = useRef<HTMLDivElement>(null);
  const viewAllRef = useRef<HTMLDivElement>(null);
  const onIndex = useCallback((i: number) => setIndex(i), [setIndex]);
  const jump = useCallback((i: number) => {
    setIndex(i, true);
    const top = deckRef.current?.getBoundingClientRect().top ?? 0;
    if (top < 0 || top > window.innerHeight * 0.6) deckRef.current?.scrollIntoView({ block: "start" });
    deckRef.current?.querySelector<HTMLElement>('[data-testid="command-deck"]')?.focus({ preventScroll: true });
  }, [setIndex]);
  const onStage = useCallback((s: Stage) => jump(Math.max(0, cmds.findIndex((c) => c.stage.id === s.id))), [cmds, jump]);
  const closeDir = useCallback(() => {
    setDirOpen(false);
    requestAnimationFrame(() => viewAllRef.current?.querySelector<HTMLElement>('[data-testid="view-all-commands"]')?.focus());
  }, []);
  const openFromDir = useCallback((c: JourneyCommand) => { setDirOpen(false); jump(cmds.indexOf(c)); }, [cmds, jump]);
  const phone = guide?.phone_e164 || null;

  return (
    <div data-testid="public-text-guide-page" className="guide-page theme-dark-scope j-page min-h-screen bg-[#171A1F] text-[#FFFFFF]">
      <div className="print-hide"><PublicHeader logo={false} /></div>
      <main className="mx-auto max-w-6xl px-5 pb-10 sm:px-8">
        <div ref={viewAllRef}><JourneyHeader g={guide} onViewAll={() => setDirOpen(true)} /></div>
        {failed && <p className="border-[3px] border-[#C92E2B] p-4 text-[#FFFFFF]" data-testid="guide-error">Couldn't load the guide. Please refresh, or text HELP.</p>}
        {!guide && !failed && <div className="h-[32rem] animate-pulse border-[3px] border-[#3A3F48]" aria-hidden />}
        {guide && cmds.length > 0 && (
          <>
            <div ref={deckRef} className="print-hide scroll-mt-[calc(var(--banner-h)+0.5rem)] lg:grid lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-8">
              <div className="mb-5 lg:sticky lg:top-[calc(var(--banner-h)+1.5rem)] lg:mb-0 lg:self-start">
                <JourneyStageNavigation active={cmds[index].stage.id} onStage={onStage} />
              </div>
              <CommandDeck cmds={cmds} index={index} phone={phone} onIndex={onIndex} onStage={onStage} />
            </div>
            <PrintSheet cmds={cmds} />
            <GuideSections g={guide} />
          </>
        )}
        <Compliance />
      </main>
      <div className="print-hide"><PublicFooter /></div>
      {dirOpen && <Suspense fallback={null}><CommandDirectory cmds={cmds} onClose={closeDir} onOpen={openFromDir} /></Suspense>}
    </div>
  );
}
